import React, { useState } from "react";
import { formatISO, subHours, subMinutes } from "date-fns";
import { Amount } from "@design-system/Amount/Amount";
import { useConstant } from "@hooks/useConstant";
import { BankNumber } from "@design-system/BankNumber/BankNumber";
import { RelativeTime } from "@design-system/RelativeTime/RelativeTime";
import { VStack } from "@design-system/VStack/VStack";
import {
    BooleanControl,
    NumberControl,
    TextControl,
    VariantGrid,
    VariantItem,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./TextFormattingPlayground.module.scss";
import gridStyles from "./playgroundControls.module.scss";

const DEFAULT_BANK_NUMBER = "021000021";

export const ValueFormattingPlayground = () => {
    const [amount, setAmount] = useState(1245.67);
    const [bankNumber, setBankNumber] = useState(DEFAULT_BANK_NUMBER);
    const [relativeHours, setRelativeHours] = useState(24);
    const [showNegative, setShowNegative] = useState(true);
    const [showPositive, setShowPositive] = useState(true);
    const [separateCurrencySign, setSeparateCurrencySign] = useState(true);
    const referenceDate = useConstant(() => new Date());

    const maskedBankNumber = `•••• ${bankNumber.slice(-4) || "0000"}`;
    const relativeDates = [
        {
            id: "minutes",
            label: "5 minutes ago",
            date: subMinutes(referenceDate, 5),
        },
        {
            id: "hours",
            label: "3 hours ago",
            date: subHours(referenceDate, 3),
        },
        {
            id: "days",
            label: "2 days ago",
            date: subHours(referenceDate, 48),
        },
        {
            id: "custom",
            label: `${relativeHours} hours ago`,
            date: subHours(referenceDate, relativeHours),
        },
    ];

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Values</h2>
                        <VStack gap={12}>
                            <NumberControl
                                label="Amount"
                                value={amount}
                                step={0.01}
                                valueWhenCleared={0}
                                onChange={setAmount}
                            />
                            <TextControl
                                label="Bank number"
                                value={bankNumber}
                                onChange={setBankNumber}
                            />
                            <NumberControl
                                label="Relative age (hours)"
                                value={relativeHours}
                                min={0}
                                valueWhenCleared={0}
                                onChange={(value) =>
                                    setRelativeHours(Math.max(0, value))
                                }
                            />
                        </VStack>
                    </section>
                    <section>
                        <h2>Amount features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Show negative sign"
                                checked={showNegative}
                                onChange={setShowNegative}
                            />
                            <BooleanControl
                                label="Show positive sign"
                                checked={showPositive}
                                onChange={setShowPositive}
                            />
                            <BooleanControl
                                label="Separate currency sign"
                                checked={separateCurrencySign}
                                onChange={setSeparateCurrencySign}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary="Amounts · account details · timestamps"
                width="full"
                output={JSON.stringify(
                    {
                        amountProps: {
                            amount,
                            showNegative,
                            showPositive,
                            separateCurrencySign,
                        },
                        bankNumberProps: {
                            value: bankNumber,
                            title: "Routing number",
                        },
                        relativeTimeProps: {
                            date: formatISO(
                                subHours(referenceDate, relativeHours),
                            ),
                        },
                        showcaseState: { maskedBankNumber },
                    },
                    null,
                    2,
                )}
            >
                <div className={gridStyles.fillWidth}>
                    <VariantGrid title="Amount" columns="auto">
                        <VariantItem label={`custom: ${amount}`}>
                            <Amount
                                amount={amount}
                                showNegative={showNegative}
                                showPositive={showPositive}
                                separateCurrencySign={separateCurrencySign}
                            />
                        </VariantItem>
                        <VariantItem label="positive">
                            <Amount amount={1245.67} showPositive />
                        </VariantItem>
                        <VariantItem label="negative">
                            <Amount amount={-1245.67} showNegative />
                        </VariantItem>
                        <VariantItem label="zero">
                            <Amount amount={0} />
                        </VariantItem>
                    </VariantGrid>
                    <VariantGrid title="BankNumber" columns="auto">
                        <VariantItem label="masked display, full copy value">
                            <BankNumber
                                value={bankNumber}
                                title="Routing number"
                            >
                                {maskedBankNumber}
                            </BankNumber>
                        </VariantItem>
                        <VariantItem label="plain value">
                            <BankNumber value={bankNumber} />
                        </VariantItem>
                    </VariantGrid>
                    <VariantGrid title="RelativeTime" columns="auto">
                        {relativeDates.map((relativeDate) => (
                            <VariantItem
                                key={relativeDate.id}
                                label={relativeDate.label}
                            >
                                <RelativeTime date={relativeDate.date} />
                            </VariantItem>
                        ))}
                    </VariantGrid>
                </div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
